import { Component, Inject, OnInit, Renderer2, HostListener, ElementRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { SidebarService } from '@shared/services/sidebar.service';
import { UserProfileModalComponent } from '@core/layout/header/user-profile-modal/user-profile-modal.component';
import { CookieService } from '@shared/services/cookie.service';
import { AuthService } from '@shared/services/auth.service';
import { TenantService } from '@core/tenant/tenant.service';
import { NotificationService } from '@shared/services/notification.service';
import { UserService } from '@shared/services/user.service';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NotificationsPanelComponent } from './notifications-panel/notifications-panel.component';


@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [MatIconButton, MatTooltip, MatIcon, NotificationsPanelComponent, TranslatePipe]
})
export class HeaderComponent implements OnInit {
  private readonly tenantService = inject(TenantService);
  private readonly notificationService = inject(NotificationService);
  private readonly userService = inject(UserService);

  isDarkMode = false;
  isMenuOpen = false;

  constructor(
    public translate: TranslateService,
    public sidebarService: SidebarService,
    private renderer: Renderer2,
    private router: Router,
    private el: ElementRef,
    private dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService
  ) { }

  get userName(): string {
    return this.authService.currentUser()?.name || 'Usuário';
  }

  get userEmail(): string {
    return this.authService.currentUser()?.email || '';
  }

  get userGroup(): string {
    return this.authService.currentUser()?.group || '';
  }

  get userBadge(): string {
    return this.userEmail ? `${this.userGroup} - ${this.userEmail}` : this.userGroup;
  }

  get profileImageUrl(): string | null {
    return this.authService.currentUser()?.profileImageUrl ?? null;
  }

  openProfileModal() {
    this.isMenuOpen = false;
    this.dialog.open(UserProfileModalComponent, {
      width: '550px',
      panelClass: 'custom-profile-modal',
    });
  }

  private loadProfile(): void {
    const userId = this.authService.currentUser()?.id;
    if (!userId) return;

    this.userService.getById(userId).subscribe({
      next: (response) => {
        this.authService.updateProfileImageUrl(response.data?.profileImagePresignedUrl ?? null);
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isMenuOpen = false;
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  logout() {
    const refreshToken = this.authService.getRefreshToken();

    this.authService.logout(refreshToken).subscribe({
      next: () => this.clearSession(),
      error: () => this.clearSession()
    });
  }

  private clearSession() {
    this.notificationService.stopConnection();
    this.authService.clearSession();
    this.router.navigate(['/login']);
  }

  get currentLogo(): string {
    return this.tenantService.getLogo(this.isDarkMode);
  }

  get currentTenantName(): string {
    return this.tenantService.tenantName();
  }

  ngOnInit(): void {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      this.isDarkMode = true;
      this.renderer.addClass(this.document.body, 'dark_mode');
    } else {
      this.renderer.removeClass(this.document.body, 'dark_mode');
    }

    this.tenantService.applyPalette(this.isDarkMode);
    this.loadProfile();
  }

  switchLanguage(lang: string) {
    this.translate.use(lang);
  }

  toggleLanguage() {
    const currentParam = this.translate.currentLang;
    const newLang = currentParam === 'en' ? 'pt' : 'en';
    this.translate.use(newLang);
  }

  toggleSidebar() {
    this.sidebarService.toggle();
  }

  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    if (this.isDarkMode) {
      this.renderer.addClass(this.document.body, 'dark_mode');
      localStorage.setItem('theme', 'dark');
    } else {
      this.renderer.removeClass(this.document.body, 'dark_mode');
      localStorage.setItem('theme', 'light');
    }

    this.tenantService.applyPalette(this.isDarkMode);
  }
}
